import { useMsal } from '@azure/msal-react'
import { faInfoCircle, faShoppingCart } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Button,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Stepper,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSessionInfo } from '../../contexts/SessionContext'
import {
  addAllLocationsToCart,
  fetchAllLocationsPreview,
  removeItemFromCart,
} from '../../data/checkout.service'
import { AllLocationsPreview } from '../../models/Checkout/AllLocationsPreview'
import { FileWithUrl } from '../../models/FileWithUrl'
import { AdDesignPrice, AdSelectionTypes, COLORS } from '../../utils/constants'
import { formatNumber } from '../../utils/displayUtils'
import { QrConfig, QrSetupStep } from '../qr/QrSetupStep'
import { AdTypeSelector } from './AdTypeSelector'
import { AdUpload } from './AdUpload'
import PaidDesignConfiguration from './PaidDesignConfiguration'

interface AllLocationsAdConfigurationProps {
  onCompletion: () => void
  onCancel: () => void
}

const durationOptions = [
  { value: '7', label: '1 Week' },
  { value: '14', label: '2 Weeks' },
  { value: '30', label: '1 Month' },
  { value: '90', label: '3 Months' },
]

function AllLocationsAdConfiguration({
  onCompletion,
  onCancel,
}: AllLocationsAdConfigurationProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [numberOfDaysToRunAd, setNumberOfDaysToRunAd] = useState<string>('30')
  const [autoRenew, setAutoRenew] = useState<boolean>(true)
  const [autoRenewMode, setAutoRenewMode] = useState<'once' | 'auto'>('auto')
  const [adSelectionType, setAdSelectionType] = useState<AdSelectionTypes>(
    AdSelectionTypes.UploadAd,
  )
  const [adName, setAdName] = useState('')
  const [adFile, setAdFile] = useState<FileWithUrl | null>(null)
  const [designNotes, setDesignNotes] = useState('')
  const [files, setFiles] = useState<FileWithUrl[]>([])
  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false)
  const [qrConfig, setQrConfig] = useState<QrConfig>({
    qrEnabled: false,
    destinationUrl: '',
    qrPositionXPercent: null,
    qrPositionYPercent: null,
    qrSizePercent: null,
  })
  const [qrHasInteracted, setQrHasInteracted] = useState(false)

  // Preview state
  const [preview, setPreview] = useState<AllLocationsPreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(false)
  // Cache previews per duration string to avoid redundant fetches
  const [previewCache, setPreviewCache] = useState<
    Record<string, AllLocationsPreview>
  >({})

  const lastStepIndex = qrConfig.qrEnabled ? 3 : 2
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { instance } = useMsal()
  const { session, setSession } = useSessionInfo()
  const navigate = useNavigate()

  const mobileStyles = {
    stepBody: { display: 'none' },
    separator: { marginLeft: -2, marginRight: -2, height: 7 },
  }

  // Pre-fetch all durations when the modal opens so prices show immediately
  useEffect(() => {
    if (!session?.organizationId) return

    let cancelled = false

    Promise.all(
      durationOptions.map((opt) =>
        fetchAllLocationsPreview(session.organizationId, parseInt(opt.value), instance)
          .then((data) => ({ value: opt.value, data }))
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return
      const newCache: Record<string, AllLocationsPreview> = {}
      results.forEach((r) => {
        if (r && r.data) newCache[r.value] = r.data
      })
      setPreviewCache((prev) => ({ ...prev, ...newCache }))
      // Seed the selected duration into preview state
      const selected = newCache[numberOfDaysToRunAd]
      if (selected) {
        setPreview(selected)
        setPreviewError(!selected.price)
      }
    })

    return () => {
      cancelled = true
    }
  }, [session?.organizationId])

  // Update the active preview when the selected duration changes (served from cache)
  useEffect(() => {
    if (!session?.organizationId) return

    const cached = previewCache[numberOfDaysToRunAd]
    if (cached) {
      setPreview(cached)
      setPreviewError(!cached.price)
      setPreviewLoading(false)
      return
    }

    // Fallback: fetch individually if somehow not in cache yet
    let cancelled = false
    setPreviewLoading(true)
    setPreviewError(false)

    fetchAllLocationsPreview(session.organizationId, parseInt(numberOfDaysToRunAd), instance)
      .then((data) => {
        if (cancelled) return
        setPreview(data)
        setPreviewCache((prev) => ({ ...prev, [numberOfDaysToRunAd]: data }))
        if (!data || data.price === 0) setPreviewError(true)
      })
      .catch(() => {
        if (cancelled) return
        setPreviewError(true)
        setPreview(null)
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [numberOfDaysToRunAd, session?.organizationId])

  const isNextButtonEnabled = (stepNumber: number): boolean => {
    if (stepNumber === 0) {
      return !previewLoading && !previewError && preview != null
    }
    if (stepNumber === 1) {
      return adSelectionType != null
    }
    if (stepNumber === 2) {
      if (adSelectionType === AdSelectionTypes.UploadAd) {
        return adFile != null
      }
      if (adSelectionType === AdSelectionTypes.PayForDesign) {
        return files.length > 0 && designNotes !== ''
      }
      return false
    }
    if (stepNumber === 3) {
      if (!qrHasInteracted) return false
      if (!qrConfig.destinationUrl) return false
      try {
        const u = new URL(qrConfig.destinationUrl)
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false
      } catch {
        return false
      }
      if (
        qrConfig.qrPositionXPercent == null ||
        qrConfig.qrPositionYPercent == null ||
        qrConfig.qrSizePercent == null
      ) {
        return false
      }
      return true
    }
    return true
  }

  const performAddToCart = async () => {
    setIsLoadingAddToCart(true)
    try {
      if (!session?.organizationId)
        throw new Error("Failed to load current user's organization")

      // Remove any existing individual cart items first
      if (session.itemsInCart.length > 0) {
        await Promise.all(
          session.itemsInCart.map((item) => removeItemFromCart(item.id, instance)),
        )
      }

      await addAllLocationsToCart(
        {
          name: adName,
          imageUrl:
            adSelectionType === AdSelectionTypes.UploadAd
              ? adFile?.url ?? ''
              : '',
          designNotes: designNotes,
          adSelectionType: adSelectionType,
          organizationId: session.organizationId,
          numberOfDaysToRunAd: parseInt(numberOfDaysToRunAd),
          autoRenew: autoRenew,
          qrEnabled: qrConfig.qrEnabled,
          destinationUrl: qrConfig.qrEnabled ? qrConfig.destinationUrl : null,
          qrPositionXPercent: qrConfig.qrEnabled ? qrConfig.qrPositionXPercent : null,
          qrPositionYPercent: qrConfig.qrEnabled ? qrConfig.qrPositionYPercent : null,
          qrSizePercent: qrConfig.qrEnabled ? qrConfig.qrSizePercent : null,
        },
        instance,
      )

      setSession({
        ...session,
        itemsInCart: [],
        allLocationsBundleCount: (session.allLocationsBundleCount ?? 0) + 1,
      })

      onCompletion()
    } catch {
      notifications.show({
        title: 'Error adding advertisement to cart',
        message:
          'Please try again. If you are unable to resolve the issue, please contact us for help',
        autoClose: false,
      })
    } finally {
      setIsLoadingAddToCart(false)
    }
  }

  const handleAddToCart = async () => {
    const existingCount = session?.itemsInCart.length ?? 0
    if (existingCount > 0) {
      modals.openConfirmModal({
        title: 'Replace cart items?',
        children: (
          <Text size='sm'>
            You have {existingCount} individual ad{existingCount !== 1 ? 's' : ''} in your cart.
            Adding an All Locations bundle will remove{' '}
            {existingCount === 1 ? 'it' : 'them'}. Do you want to continue?
          </Text>
        ),
        labels: { confirm: 'Yes, continue', cancel: 'Cancel' },
        confirmProps: { style: { background: COLORS.HiddenGemzRed } },
        onConfirm: performAddToCart,
      })
    } else {
      await performAddToCart()
    }
  }

  const nextStep = async () => {
    if (currentStep === lastStepIndex) {
      await handleAddToCart()
    } else {
      setCurrentStep((c) => c + 1)
    }
  }

  const prevStep = () => {
    if (currentStep === 0) {
      onCancel()
    } else {
      setCurrentStep((c) => c - 1)
    }
  }

  const designFee =
    adSelectionType === AdSelectionTypes.PayForDesign ? AdDesignPrice : 0
  const totalPrice = preview ? preview.price + designFee : null

  return (
    <Stack>
      <Stepper
        active={currentStep}
        onStepClick={setCurrentStep}
        styles={{
          content: { paddingTop: 10 },
          ...(isMobile ? mobileStyles : {}),
        }}
        size='sm'
      >
        {/* Step 0 — Ad Run Length */}
        <Stepper.Step
          label='Ad Run Length'
          description={
            durationOptions.find((o) => o.value === numberOfDaysToRunAd)?.label
          }
          style={{ justifyItems: 'center' }}
        >
          <Group
            gap='sm'
            my={isMobile ? 10 : 20}
            pt={isMobile ? 0 : 10}
            w='100%'
            wrap='wrap'
            grow
            justify='center'
          >
            {durationOptions.map((option) => {
              const cached = previewCache[option.value]
              const optionPrice = cached?.price
              return (
                <Button
                  key={option.value}
                  variant={
                    numberOfDaysToRunAd === option.value ? 'filled' : 'outline'
                  }
                  color={
                    numberOfDaysToRunAd === option.value
                      ? COLORS.LightGreen
                      : 'gray'
                  }
                  onClick={() => setNumberOfDaysToRunAd(option.value)}
                  radius='md'
                  size='md'
                  style={{ minHeight: 80, minWidth: 100 }}
                >
                  <Stack gap={4} align='center'>
                    <Text>{option.label}</Text>
                    {optionPrice != null && optionPrice > 0 && (
                      <Text
                        size='sm'
                        fz={16}
                        fw={700}
                        c={
                          numberOfDaysToRunAd === option.value
                            ? 'white'
                            : COLORS.LightGreen
                        }
                      >
                        ${formatNumber(optionPrice)}
                      </Text>
                    )}
                  </Stack>
                </Button>
              )
            })}
          </Group>

          <Stack gap={isMobile ? 8 : 2} pt={isMobile ? 16 : 10}>
            <SegmentedControl
              value={autoRenewMode}
              onChange={(value) => {
                const v = value as 'once' | 'auto'
                setAutoRenewMode(v)
                setAutoRenew(v === 'auto')
              }}
              data={[
                { label: 'Run Once', value: 'once' },
                { label: 'Auto-Renew', value: 'auto' },
              ]}
              color={COLORS.LightGreen}
              size={isMobile ? 'md' : 'sm'}
              fullWidth={isMobile}
              mt={isMobile ? 8 : 0}
            />
            {autoRenewMode === 'auto' && (
              <Text size='sm' c='dimmed' pt={2}>
                *The card on file will be charged each time the ad renews, 1
                day before ending.
              </Text>
            )}
          </Stack>

          {/* Preview summary strip */}
          <Group
            mt={16}
            p={12}
            style={{
              background: '#f6f6f6',
              borderRadius: 8,
              border: '1px solid #e0e0e0',
            }}
            gap='xl'
          >
            {previewLoading ? (
              <Loader size='sm' />
            ) : previewError ? (
              <Text size='sm' c='red'>
                Pricing not configured for this duration
              </Text>
            ) : preview ? (
              <>
                <Text size='sm'>
                  <Text span fw={700}>
                    {preview.eligibleDeviceCount}
                  </Text>{' '}
                  eligible devices
                </Text>
                <Group gap={4} align='center'>
                  <Text size='sm'>
                    <Text span fw={700}>
                      {preview.excludedDeviceCount}
                    </Text>{' '}
                    excluded
                  </Text>
                  <Tooltip
                    label='These screens belong to business owners who have chosen to prevent ads from your business category.'
                    multiline
                    w={240}
                    withArrow
                    events={{ hover: true, focus: true, touch: true }}
                  >
                    <span style={{ cursor: 'pointer', lineHeight: 1, color: '#aaa' }}>
                      <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: 13 }} />
                    </span>
                  </Tooltip>
                </Group>
              </>
            ) : null}
          </Group>

        </Stepper.Step>

        {/* Step 1 — Design Type */}
        <Stepper.Step
          label='Design Type'
          description={adSelectionType}
          allowStepSelect={isNextButtonEnabled(0)}
        >
          <AdTypeSelector
            setAdSelectionType={(type) => {
              setAdSelectionType(type)
              if (type === AdSelectionTypes.PayForDesign) {
                setQrConfig((prev) => ({ ...prev, qrEnabled: false }))
              }
            }}
            adSelectionType={adSelectionType}
            requiresApproval={false}
            isMobile={isMobile}
          />
        </Stepper.Step>

        {/* Step 2 — Ad Details */}
        <Stepper.Step
          label='Ad Details'
          description='Please add details'
          allowStepSelect={isNextButtonEnabled(0)}
        >
          <Stack>
            <TextInput
              radius='md'
              label='Ad Name'
              placeholder='Summer Blowout Sale'
              value={adName}
              styles={{ input: { fontSize: '16px' } }}
              onChange={(e) => setAdName(e.target.value)}
            />
            {adSelectionType === AdSelectionTypes.UploadAd && (
              <AdUpload
                onFileUploadComplete={setAdFile}
                adFile={adFile}
                showCanvaText={true}
              />
            )}
            {adSelectionType === AdSelectionTypes.PayForDesign && (
              <PaidDesignConfiguration
                setFiles={setFiles}
                designNotes={designNotes}
                setDesignNotes={setDesignNotes}
              />
            )}
            {adSelectionType === AdSelectionTypes.UploadAd && (
              <SegmentedControl
                value={qrConfig.qrEnabled ? 'enabled' : 'disabled'}
                onChange={(value) => {
                  const enabling = value === 'enabled'
                  if (enabling) setQrHasInteracted(false)
                  setQrConfig({ ...qrConfig, qrEnabled: enabling })
                }}
                data={[
                  { label: 'QR Scan Tracking Disabled', value: 'disabled' },
                  { label: 'QR Scan Tracking Enabled', value: 'enabled' },
                ]}
                color={COLORS.LightGreen}
                size={isMobile ? 'md' : 'sm'}
                fullWidth
              />
            )}
          </Stack>
        </Stepper.Step>

        {/* Step 2 — QR Code (conditional) */}
        {qrConfig.qrEnabled && (
          <Stepper.Step
            label='QR Code'
            description='Position & URL'
            allowStepSelect={isNextButtonEnabled(0) && isNextButtonEnabled(1) && isNextButtonEnabled(2)}
          >
            <QrSetupStep
              config={qrConfig}
              onChange={setQrConfig}
              baseImageUrl={adFile?.url ?? null}
              showToggle={false}
              onInteracted={() => setQrHasInteracted(true)}
            />
          </Stepper.Step>
        )}
      </Stepper>

      {/* Sticky footer */}
      <Stack
        gap={0}
        pos='sticky'
        bg='white'
        pt={10}
        style={{ bottom: 0, borderTop: '1px solid lightgray' }}
      >
        <Stack gap={0} align='flex-end'>
          {adSelectionType === AdSelectionTypes.PayForDesign && (
            <Text>+ ${AdDesignPrice} Design Fee</Text>
          )}
          {totalPrice != null && (
            <Title size='h3' style={{ alignSelf: 'flex-end' }}>
              Total: ${formatNumber(totalPrice)}
            </Title>
          )}
        </Stack>

        <Group justify='space-between' mt='sm' mb='sm'>
          <Button
            variant='default'
            onClick={prevStep}
            radius='md'
            disabled={isLoadingAddToCart}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            disabled={
              !isNextButtonEnabled(currentStep) || isLoadingAddToCart
            }
            onClick={nextStep}
            radius='md'
          >
            {currentStep === lastStepIndex ? (
              <Group gap={8}>
                {isLoadingAddToCart ? (
                  <Loader size='xs' />
                ) : (
                  <FontAwesomeIcon icon={faShoppingCart} />
                )}
                <Text>Add To Cart</Text>
              </Group>
            ) : (
              <Text>Next</Text>
            )}
          </Button>
        </Group>
      </Stack>
    </Stack>
  )
}

export default AllLocationsAdConfiguration
